import type {
    CheckoutPaymentInput,
    CheckoutRecurringInput
} from '~/resources/graphql/graphql'
import type { PaymentInformationSchemaType } from '~/components/Checkout/Sections/PaymentInformation.vue'
import type { RecurringInformationSchemaType } from '~/components/Checkout/Sections/RecurringInformation.vue'

export function checkoutRecurringInput(
    value: RecurringInformationSchemaType
): CheckoutRecurringInput {
    const recurring: CheckoutRecurringInput = {
        total: value.total,
        creditCard: null,
        bankAccount: null,
        storedPaymentMethod: null
    }

    if (value.paymentMethod.type === 'CreditCard') {
        recurring.creditCard = {
            paymentSourceId: value.paymentMethod.paymentSourceId,
            token: value.paymentMethod.token
        }
    } else if (value.paymentMethod.type === 'BankAccount') {
        recurring.bankAccount = {
            paymentSourceId: value.paymentMethod.paymentSourceId,
            routingNumber: value.paymentMethod.routingNumber,
            accountNumber: value.paymentMethod.accountNumber,
            accountNumberConfirm: value.paymentMethod.accountNumberConfirm,
            accountType: value.paymentMethod.accountType
        }
    } else if (value.paymentMethod.type === 'StoredPaymentMethod') {
        recurring.storedPaymentMethod = {
            paymentSourceId: value.paymentMethod.paymentSourceId,
            id: value.paymentMethod.id
        }
    } else {
        throw new Error(
            'Unknown Recurring Payment Method Type: ' + value.paymentMethod.type
        )
    }

    return recurring
}

export function checkoutPaymentInput(
    value: PaymentInformationSchemaType
): CheckoutPaymentInput {
    // Payment Items
    const payment = {
        items: [] as {
            paymentSourceId: string
            amount: string
            token: string
        }[]
    }

    // Add any gift cards
    if (value.giftCards.length > 0) {
        payment.items.push(
            ...value.giftCards.map(giftCard => {
                return {
                    paymentSourceId: giftCard.paymentSourceId,
                    amount: giftCard.amount,
                    token: giftCard.token
                }
            })
        )
    }

    // Add any store credit
    if (value.storeCredit !== null) {
        payment.items.push({
            paymentSourceId: value.storeCredit.paymentSourceId,
            amount: value.storeCredit.amountUsed,
            token: 'STORE_CREDIT'
        })
    }

    // Remaining payment amount
    if (value.paymentMethod !== null) {
        if (value.paymentMethod.type === 'RecurringPaymentMethod') {
            payment.items.push({
                paymentSourceId: value.paymentMethod.paymentSourceId,
                amount: value.paymentMethod.amount,
                token: 'RECURRING_PAYMENT_METHOD'
            })
        } else if (value.paymentMethod.type === 'StoredPaymentMethod') {
            payment.items.push({
                paymentSourceId: value.paymentMethod.paymentSourceId,
                amount: value.paymentMethod.amount,
                token: 'RP_SPM|' + value.paymentMethod.id
            })
        } else if (value.paymentMethod.type === 'PaymentMethod') {
            payment.items.push({
                paymentSourceId: value.paymentMethod.paymentSourceId,
                amount: value.paymentMethod.amount,
                token: value.paymentMethod.token
            })
        } else {
            throw new Error('Unknown payment type')
        }
    }

    return payment
}

export function resetPaymentErrors(
    setErrors: (errors: Record<string, undefined>) => void
) {
    // Also clear this error so we don't flash red on the payment error
    setErrors({
        payment: undefined,
        'payment.paymentMethod.amount': undefined,
        'payment.paymentMethod.id': undefined,
        'payment.paymentMethod.paymentSourceId': undefined,
        'payment.paymentMethod.token': undefined,
        'payment.paymentMethod.type': undefined,
        'payment.storeCredit.amountUsed': undefined,
        'payment.storeCredit.paymentSourceId': undefined,
        'payment.total': undefined
    })
}
